// @ts-nocheck

/**
 * MSI3 Report Service
 * 
 * Generates monthly revenue reports for facilities.
 * Exports to Excel format for payment processing.
 */

import { db } from '../firebase';
import { MSI3_CONSTANTS } from './constants';

const { REVENUE_STATUS } = MSI3_CONSTANTS;

export const reportService = {
  /**
   * Generate monthly revenue report data for a facility
   * @param {string} facilityPartnerId - Facility partnership ID
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @returns {Promise<Object>} Report data
   */
  async generateMonthlyReport(facilityPartnerId, year, month) {
    // Get facility info
    const facilityDoc = await db.collection('msi3FacilityPartners').doc(facilityPartnerId).get();
    if (!facilityDoc.exists) {
      throw new Error('Facility not found');
    }
    const facilityData = facilityDoc.data();

    // Get facility user for display name
    const facilityUserDoc = await db.collection('users').doc(facilityData.facilityUserId).get();
    const facilityName = facilityUserDoc.data()?.name || 'Unknown Facility';

    // Get city info
    const cityDoc = await db.collection('msi3PopulationCenters').doc(facilityData.cityId).get();
    const cityName = cityDoc.data()?.cityName || 'Unknown City';

    // Get all patients where this facility earns revenue
    const enrollmentsSnapshot = await db
      .collection('msi3PatientEnrollments')
      .where('currentRevenueFacilityId', '==', facilityPartnerId)
      .where('revenueShareStatus', '==', REVENUE_STATUS.ACTIVE)
      .get();

    const enrollments = enrollmentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Calculate statistics
    let totalRevenue = 0;
    let inMarketCount = 0;
    let outOfMarketCount = 0;
    const tierBreakdown = { free: 0, small: 0, medium: 0, large: 0 };

    const patientDetails = await Promise.all(enrollments.map(async (enrollment) => {
      const daysActive = this.calculateDaysActiveInMonth(enrollment, year, month);
      const amountDue = (enrollment.lockedMonthlyAmount / 30) * daysActive;
      totalRevenue += amountDue;

      if (enrollment.countsTowardMarketPenetration) {
        inMarketCount++;
      } else {
        outOfMarketCount++;
      }

      tierBreakdown[enrollment.subscriptionTier]++;

      // Get patient email (anonymized)
      const patientDoc = await db.collection('users').doc(enrollment.patientUserId).get();
      const patientEmail = patientDoc.data()?.email || 'unknown';
      const anonymizedEmail = patientEmail.includes('@')
        ? patientEmail.split('@')[0].substring(0, 3) + '***@' + patientEmail.split('@')[1]
        : 'unknown';

      return {
        patientId: enrollment.patientUserId.substring(0, 8),
        anonymizedEmail,
        subscriptionTier: enrollment.subscriptionTier,
        enrollmentDate: enrollment.enrollmentDate?.toDate?.()?.toISOString().split('T')[0] || 'N/A',
        lockedRevenueShare: enrollment.lockedRevenueSharePercentage,
        monthlyAmount: enrollment.lockedMonthlyAmount,
        inMarket: enrollment.countsTowardMarketPenetration,
        daysActive,
        amountDue: Math.round(amountDue * 100) / 100,
      };
    }));

    return {
      meta: {
        facilityId: facilityPartnerId,
        facilityName,
        cityName,
        period: { year, month },
        generatedAt: new Date().toISOString(),
      },
      summary: {
        totalPatients: enrollments.length,
        inMarketPatients: inMarketCount,
        outOfMarketPatients: outOfMarketCount,
        tierBreakdown,
        totalRevenue: Math.round(totalRevenue * 100) / 100,
      },
      patients: patientDetails,
    };
  },

  /**
   * Calculate days a patient was active in a specific month
   */
  calculateDaysActiveInMonth(enrollment, year, month) {
    const enrollmentDate = enrollment.enrollmentDate?.toDate?.()
      || new Date(enrollment.enrollmentDate);

    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);

    const activeStart = enrollmentDate > monthStart ? enrollmentDate : monthStart;
    const activeEnd = monthEnd;

    if (activeStart > activeEnd) return 0;

    const daysActive = Math.ceil((activeEnd - activeStart) / (1000 * 60 * 60 * 24)) + 1;
    return Math.min(daysActive, monthEnd.getDate());
  },

  /**
   * Convert report data to CSV format
   */
  reportToCSV(reportData) {
    const { meta, summary, patients } = reportData;

    // Header info
    const headerLines = [
      `MSI3 Revenue Report - ${meta.facilityName}`,
      `City: ${meta.cityName}`,
      `Period: ${meta.period.month}/${meta.period.year}`,
      `Generated: ${meta.generatedAt}`,
      '',
      'SUMMARY',
      `Total Patients,${summary.totalPatients}`,
      `In-Market Patients,${summary.inMarketPatients}`,
      `Out-of-Market Patients,${summary.outOfMarketPatients}`,
      `Free Tier,${summary.tierBreakdown.free}`,
      `Small Tier,${summary.tierBreakdown.small}`,
      `Medium Tier,${summary.tierBreakdown.medium}`,
      `Large Tier,${summary.tierBreakdown.large}`,
      `Total Revenue,$${summary.totalRevenue.toFixed(2)}`,
      '',
      'PATIENT DETAILS',
    ];

    // Patient table headers
    const patientHeaders = [
      'Patient ID',
      'Email (Anonymized)',
      'Subscription Tier',
      'Enrollment Date',
      'Revenue Share %',
      'Monthly Amount',
      'In Market',
      'Days Active',
      'Amount Due',
    ].join(',');

    // Patient rows
    const patientRows = patients.map(p => [
      p.patientId,
      p.anonymizedEmail,
      p.subscriptionTier,
      p.enrollmentDate,
      p.lockedRevenueShare + '%',
      '$' + p.monthlyAmount.toFixed(2),
      p.inMarket ? 'Yes' : 'No',
      p.daysActive,
      '$' + p.amountDue.toFixed(2),
    ].join(','));

    return [...headerLines, patientHeaders, ...patientRows].join('\n');
  },

  /**
   * Download report as CSV file
   */
  downloadReportCSV(reportData) {
    const csv = this.reportToCSV(reportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `msi3-revenue-${reportData.meta.facilityName.replace(/\s+/g, '_')}-${reportData.meta.period.year}-${String(reportData.meta.period.month).padStart(2, '0')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    window.URL.revokeObjectURL(url);
  },

  /**
   * Get available report periods for a facility
   * Returns array of {year, month} objects for the last 12 months
   */
  getAvailablePeriods() {
    const periods = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      periods.push({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      });
    }
    
    return periods;
  },
};

